import { type ReactNode } from "react";

/**
 * A data table for the navy/blue auth/admin screens (controlplane-auth task 11.2, echoing AgentDNA's
 * `DataTable`). The existing dark SPA has `DenseTable` (`components/DenseTable.tsx`); this is its
 * `.oa-auth` sibling for the member-management surface (task 13), with the one extra affordance those
 * screens need: an optional per-row click (open a member drawer) that stays keyboard-operable.
 *
 * Accessibility (design 12.4): a real `<table>` with a `<caption>`, `<th scope="col">` headers and an
 * optional `<th scope="row">`. When a row is clickable it becomes a `<tr>` with `role="button"`,
 * `tabIndex=0` and Enter/Space handling, with a visible focus ring from the scoped CSS — so a mouse
 * is never required. An honest empty state renders an explanatory message inside the table rather
 * than a bare header.
 *
 * It is generic over the row type `T` and presentational: a column says how to render its cell; the
 * table does no fetching and no sorting (the caller orders rows). Cells may return any `ReactNode`
 * (a pill, a `MetricTile`, text).
 */

export interface DataColumn<T> {
  /** Stable key for React and `aria`. */
  readonly key: string;
  /** The visible column header. */
  readonly header: ReactNode;
  /** Render this column's cell for a row. */
  readonly cell: (row: T, rowIndex: number) => ReactNode;
  /** Mark this column's cell as the row header (`<th scope="row">`). At most one column. */
  readonly isRowHeader?: boolean;
  /** `right` for numeric columns; defaults to `left`. */
  readonly align?: "left" | "right";
  /** Optional width hint for the scoped CSS (`data-width`). */
  readonly width?: "sm" | "md" | "lg";
}

export interface DataTableProps<T> {
  /** Accessible caption describing the table. */
  readonly caption: ReactNode;
  /** Visually hide the caption while keeping it for assistive tech. Default false. */
  readonly captionHidden?: boolean;
  /** The columns, left to right. */
  readonly columns: readonly DataColumn<T>[];
  /** The rows, already ordered/filtered by the caller. */
  readonly rows: readonly T[];
  /** A stable key for a row. Defaults to the row index. */
  readonly rowKey?: (row: T, index: number) => string;
  /** Shown when `rows` is empty — explains the cause (design 12.3). */
  readonly emptyMessage?: string;
  /** Keep the header fixed while the body scrolls. Default false. */
  readonly stickyHeader?: boolean;
  /**
   * Optional per-row activation. When set, each row is keyboard-focusable and activates on
   * click / Enter / Space (e.g. open the member drawer). The accessible name for the row comes from
   * {@link rowLabel}.
   */
  readonly onRowActivate?: (row: T, index: number) => void;
  /** The accessible name for a clickable row; required when `onRowActivate` is set. */
  readonly rowLabel?: (row: T, index: number) => string;
}

export function DataTable<T>(props: DataTableProps<T>): ReactNode {
  const { columns, rows } = props;
  const rowKey = props.rowKey ?? ((_row: T, index: number): string => String(index));
  const isEmpty = rows.length === 0;
  const clickable = props.onRowActivate !== undefined;

  const activate = (row: T, index: number): void => {
    props.onRowActivate?.(row, index);
  };

  return (
    <div className="oa-table-wrap">
      <table
        className="oa-table"
        data-sticky={props.stickyHeader === true ? "true" : undefined}
        data-clickable={clickable ? "true" : undefined}
      >
        <caption className={props.captionHidden === true ? "oa-sr-only" : undefined}>
          {props.caption}
        </caption>
        <thead>
          <tr>
            {columns.map((col) => (
              <th key={col.key} scope="col" data-align={col.align ?? "left"} data-width={col.width}>
                {col.header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {isEmpty ? (
            <tr className="oa-table__empty-row">
              <td colSpan={columns.length}>
                <span className="oa-table__empty">
                  {props.emptyMessage ?? "Nothing to show."}
                </span>
              </td>
            </tr>
          ) : (
            rows.map((row, rowIndex) => {
              const rowProps = clickable
                ? {
                    role: "button",
                    tabIndex: 0,
                    "aria-label": props.rowLabel?.(row, rowIndex),
                    onClick: () => activate(row, rowIndex),
                    onKeyDown: (event: React.KeyboardEvent<HTMLTableRowElement>) => {
                      if (event.key === "Enter" || event.key === " ") {
                        event.preventDefault();
                        activate(row, rowIndex);
                      }
                    },
                  }
                : {};
              return (
                <tr key={rowKey(row, rowIndex)} {...rowProps}>
                  {columns.map((col) => {
                    const content = col.cell(row, rowIndex);
                    if (col.isRowHeader === true) {
                      return (
                        <th key={col.key} scope="row" data-align={col.align ?? "left"}>
                          {content}
                        </th>
                      );
                    }
                    return (
                      <td key={col.key} data-align={col.align ?? "left"}>
                        {content}
                      </td>
                    );
                  })}
                </tr>
              );
            })
          )}
        </tbody>
      </table>
    </div>
  );
}
